<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import ProductNew from "@/components/home/ProductNew.vue";
import BaseLink from "@/components/buttons/BaseLink.vue";
import { vElementHover } from "@vueuse/components";
import { HomeService } from "@/services/HomeService";
import { Jsona } from "jsona";
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  EffectFade,
} from "swiper/modules";
import { useIntersectionObserver } from "@vueuse/core";

const dataFormatter = new Jsona();
const viewportWidth = ref(0);
const viewportHeight = ref(0);
const products = ref<unknown[]>([]);
const isHovered = ref(false);
const linkSeeMore = ref("/");
const modules = [Navigation, Pagination, A11y, EffectFade, Scrollbar];

const updateViewportDimensions = () => {
  viewportWidth.value = window.innerWidth;
  viewportHeight.value = window.innerHeight;
};

onMounted(() => {
  window.addEventListener("resize", updateViewportDimensions);
  updateViewportDimensions();
});

onUnmounted(() => {
  window.removeEventListener("resize", updateViewportDimensions);
});

const styleResponsive = computed(() => {
  return viewportWidth.value <= 992
    ? "border-0"
    : "shadow border border-light-subtle";
});

const onHover = (state: boolean) => {
  isHovered.value = state;
};

const search = () => {
  const homeService = new HomeService();
  homeService.getNewProducts().then((response) => {
    if (response) {
      products.value = dataFormatter.deserialize(response) as unknown[];
      if (response.links?.self) {
        linkSeeMore.value = response.links.self;
      }
    }
  });
};

const section = ref<HTMLElement>();
let searched = false;
useIntersectionObserver(section, ([observer]) => {
  if (!searched && observer.isIntersecting) {
    searched = true;
    search();
  }
});
</script>
<template>
  <section ref="section">
    <div v-if="products.length > 0" class="mt-2 mt-lg-0 new-products">
      <p class="text-secondary title px-3 px-lg-0">
        Conoce los productos nuevos
      </p>
      <div class="px-2">
        <div class="row" :class="styleResponsive">
          <div class="col-12 col-lg-10 col-xl-11">
            <swiper
              :modules="modules"
              :navigation="false"
              :loop="true"
              :pagination="false"
              :scrollbar="{ draggable: true }"
              :breakpoints="{
                '320': {
                  slidesPerView: 3,
                  spaceBetween: 0,
                },
                '640': {
                  slidesPerView: 4,
                  spaceBetween: 0,
                },
                '768': {
                  slidesPerView: 4,
                  spaceBetween: 0,
                },
                '992': {
                  slidesPerView: 6,
                  spaceBetween: 0,
                },
                '1280': {
                  slidesPerView: 8,
                  spaceBetween: 0,
                },
                '1440': {
                  slidesPerView: 9,
                  spaceBetween: 0,
                },
                '1700': {
                  slidesPerView: 10,
                  spaceBetween: 0,
                },
              }"
              class="p-0"
            >
              <swiper-slide
                v-for="(product, index) in products"
                :key="index"
                class="my-auto align-self-stretch"
              >
                <product-new
                  :product="product as Record<string, any>"
                ></product-new>
              </swiper-slide>
            </swiper>
          </div>
          <div
            class="col-lg-2 col-xl-1 d-none d-lg-flex align-items-center justify-content-md-end justify-content-xl-start"
          >
            <base-link
              v-element-hover="onHover"
              class="d-flex"
              :class="{ 'shadow-inset text-danger': isHovered }"
              :to="linkSeeMore"
            >
              <span class="fs-6 text-nowrap text-secondary mt-1">
                Ver todos
              </span>
              <i class="bi bi-chevron-double-right fs-3 text-secondary" />
            </base-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style lang="sass" scoped>
.swiper
    width: 100%

.swiper-slide
    text-align: center
    font-size: 18px
    background: #fff
    display: flex
    justify-content: center
    align-items: center

.shadow-inset
    box-shadow: inset 0px 1px 5px var(--bs-gray-200)
</style>
